import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { SelectionSource } from '@/types/domain';

// ── Favorites ──────────────────────────────────────────────

interface FavoritesState {
  ids: string[];
  isFavorite: (id: string) => boolean;
  toggle: (id: string) => void;
}

export const useFavoritesStore = create<FavoritesState>()(
  persist(
    (set, get) => ({
      ids: [],
      isFavorite: (id) => get().ids.includes(id),
      toggle: (id) =>
        set((s) => ({
          ids: s.ids.includes(id)
            ? s.ids.filter((x) => x !== id)
            : [...s.ids, id],
        })),
    }),
    { name: 'radioatlas-favorites' },
  ),
);

// ── History ────────────────────────────────────────────────

export interface HistoryEntry {
  id: string;
  stationId: string;
  stationName: string;
  source: SelectionSource;
  startedAt: string;
  durationSec: number;
}

interface HistoryState {
  entries: HistoryEntry[];
  activeSessionId: string | null;
  activeStartedAt: number | null;
  addEntry: (entry: HistoryEntry) => void;
  startSession: (
    stationId: string,
    stationName: string,
    source: SelectionSource,
  ) => void;
  endActiveSession: () => void;
  clear: () => void;
}

export const useHistoryStore = create<HistoryState>()(
  persist(
    (set, get) => ({
      entries: [],
      activeSessionId: null,
      activeStartedAt: null,
      addEntry: (entry) =>
        set((s) => ({
          entries: [entry, ...s.entries].slice(0, 50),
        })),
      startSession: (stationId, stationName, source) => {
        get().endActiveSession();

        const entry: HistoryEntry = {
          id: `${stationId}-${Date.now()}`,
          stationId,
          stationName,
          source,
          startedAt: new Date().toISOString(),
          durationSec: 0,
        };

        set((s) => ({
          entries: [entry, ...s.entries].slice(0, 50),
          activeSessionId: entry.id,
          activeStartedAt: Date.now(),
        }));
      },
      endActiveSession: () => {
        const { activeSessionId, activeStartedAt } = get();
        if (!activeSessionId || !activeStartedAt) return;

        const durationSec = Math.max(0, Math.round((Date.now() - activeStartedAt) / 1000));
        set((state) => ({
          entries: state.entries.map((entry) =>
            entry.id === activeSessionId ? { ...entry, durationSec } : entry,
          ),
          activeSessionId: null,
          activeStartedAt: null,
        }));
      },
      clear: () => {
        set({ entries: [], activeSessionId: null, activeStartedAt: null });
      },
    }),
    {
      name: 'radioatlas-history',
      partialize: (s) => ({ entries: s.entries }),
    },
  ),
);
